import Keycloak from 'keycloak-js';

export const kc = new Keycloak({
  url: '/auth',
  realm: 'bcid',
  clientId: 'bcid-portal-spa',
});

let refreshTimer: ReturnType<typeof setInterval> | null = null;

// Refresh cadence vs the realm's token settings (both 900s = 15 min):
// accessTokenLifespan AND ssoSessionIdleTimeout. Only a Keycloak refresh
// resets the SSO idle clock — API activity does not — so the refresh MUST
// fire at least one tick before expiry. minValidity (seconds) therefore has
// to exceed the check interval: with a 5-minute interval, 330s makes the
// 10-minute tick refresh (5 min token left < 330s), leaving 5 minutes of
// idle slack. The old value (60) meant the first refresh attempt landed
// exactly at the 15-minute boundary where the token AND the SSO idle window
// expire together — every session died there regardless of user activity
// (2026-07-10 incident: zero REFRESH_TOKEN events in the realm, only
// repeated LOGINs). Sessions remain capped by ssoSessionMaxLifespan (8h).
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const REFRESH_MIN_VALIDITY_S = 330;

// #399 — self-service password and MFA, via Keycloak Application-Initiated
// Actions. `kc.login({ action })` re-enters the authorization endpoint with
// `kc_action=<ACTION>`, which renders that single action through the realm's
// LOGIN theme — already BCID-branded, already reachable through nginx.
//
// Deliberately NOT the Keycloak v3 Account Console: it is a separate SPA that
// our CSP blocks (finding V-S1), and it would be a second UI to brand and
// maintain. Nothing here handles a credential; the browser leaves for
// Keycloak and comes back.
export const AIA_ACTIONS = {
  changePassword: 'UPDATE_PASSWORD',
  configureTotp: 'CONFIGURE_TOTP',
} as const;

export type AiaAction = (typeof AIA_ACTIONS)[keyof typeof AIA_ACTIONS];

/**
 * Send the user to Keycloak to perform one action, then back here.
 *
 * `redirectUri` defaults to the CURRENT URL, which is what makes the
 * round-trip return to the page they left rather than to the app root. The SPA
 * has no router — the page is React state, not a URL — so "the page they left"
 * is as precise as this can be, and landing back on the app with their session
 * intact is the part that matters.
 *
 * Returns nothing useful: the call navigates away. Anything a caller does
 * after awaiting it will not run in the common case.
 */
export function startAccountAction(action: AiaAction, redirectUri: string = window.location.href): void {
  void kc.login({ action, redirectUri });
}

// #419 — the root URL serves this, the STAFF bundle, but a customer-only
// account authenticates against the staff client perfectly well: Keycloak keeps
// one SSO session per realm, so both a fresh sign-in at the bare domain and a
// new tab opened while signed in at /apply/ arrive here holding a valid token
// with no staff role. That used to render the staff shell, whose every call
// 403s — "failed to load cases: forbidden", with no hint that /apply/ is home.

// The four staff realm roles, mirroring deriveAppRole() in
// apps/api/src/auth/role.ts — the function that actually decides whether a
// token satisfies a staff guard, so this list is what "is staff" means. It must
// track APP_ROLES over there — this is now the third copy of the four names
// (role.ts, useUser.ts's per-capability checks, here) and nothing enforces the
// lockstep. Drift is fail-safe in this direction: a staff role missing here
// cannot redirect anyone, because the redirect also requires the customer role.
//
// Deliberately an explicit list and NOT a `bcid-` prefix test. The prefix is a
// naming convention, not the rule the API applies, and the customer roles are
// non-prefixed precisely so deriveAppRole() returns null for them (see Step
// 4.20 in docs/deployment/04-keycloak-configure.md). A future bcid-prefixed
// realm role that is not a staff role would, under a prefix test, strand
// customers back in the forbidden shell this exists to prevent.
const STAFF_REALM_ROLES = ['bcid-admin', 'bcid-analyst', 'bcid-approver', 'bcid-legal'];

// The realm role that admits a human to /apply/. Held alongside exactly one
// customer-portal-{viewer,submitter,admin} tier; the tier is the portal's
// business and is not consulted here — any customer login belongs at /apply/.
const CUSTOMER_PORTAL_ROLE = 'customer-portal-user';

const CUSTOMER_PORTAL_PATH = '/apply/';

/**
 * Which app this token belongs to, given that it landed on the root URL.
 *
 * - `staff` — holds a staff realm role. Mixed accounts included: staff wins at
 *   the root, and /apply/ stays reachable by going there directly.
 * - `customer` — no staff role, but admitted to the portal. This login has a
 *   home and it is not here.
 * - `neither` — falls through to the existing denied handling (the shell's own
 *   error state over the API's 403s), which is what a token carrying no app
 *   role has always got.
 */
export function rootDestination(): 'staff' | 'customer' | 'neither' {
  const t = kc.tokenParsed as { realm_access?: { roles?: string[] } } | undefined;
  const roles = t?.realm_access?.roles ?? [];
  if (roles.some((r) => STAFF_REALM_ROLES.includes(r))) return 'staff';
  return roles.includes(CUSTOMER_PORTAL_ROLE) ? 'customer' : 'neither';
}

export async function initKeycloak(): Promise<void> {
  const authenticated = await kc.init({
    onLoad: 'login-required',
    pkceMethod: 'S256',
    checkLoginIframe: false,
  });
  if (!authenticated) throw new Error('keycloak: not authenticated after init');

  // #419 — send a customer-only login to /apply/ before anything staff-shaped
  // happens. `replace`, not `assign`, so Back does not bounce them straight
  // back to a shell that 403s.
  //
  // The never-settling promise is the load-bearing half. replace() only
  // *schedules* a navigation — the calling script runs on until it commits —
  // so returning normally here would still let main.tsx fetch /api/v1/me and
  // mount <App/>, which is the doomed staff request and the flash of staff UI
  // this is meant to avoid. Never settling stops the bootstrap dead instead,
  // and needs no cooperation from the caller. Placed above the refresh timer so
  // no interval is left armed on a page that is leaving.
  //
  // The already-there guard is for the dev server only. In production it can
  // never fire: nginx serves the portal bundle under /apply/, so this code is
  // not running at that path. Vite has no /apply/ route, and its SPA fallback
  // hands the staff index to every path — so without the guard, a customer
  // token at 127.0.0.1:5173/apply/ replaces onto itself and reloads forever.
  // Falling through to the staff shell there is the pre-#419 behaviour: wrong,
  // but a page you can read and close rather than a hang.
  const alreadyAtPortal = window.location.pathname.startsWith(CUSTOMER_PORTAL_PATH);
  if (rootDestination() === 'customer' && !alreadyAtPortal) {
    window.location.replace(CUSTOMER_PORTAL_PATH);
    await new Promise<never>(() => {
      /* intentionally never settles — see above */
    });
  }

  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = setInterval(async () => {
    try {
      await kc.updateToken(REFRESH_MIN_VALIDITY_S);
    } catch {
      // One retry so a transient network blip doesn't end the session; a
      // second failure means the session is genuinely gone (revoked,
      // idle-expired, disabled) — bounce to login.
      try {
        await kc.updateToken(REFRESH_MIN_VALIDITY_S);
      } catch {
        kc.logout();
      }
    }
  }, REFRESH_INTERVAL_MS);
}
